// src/components/ProtectedRoute.jsx
// LOOP AUTH OPTIMAL R31 — protects authentication and, by default, requires a complete profile.

import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { auth } from "../firebase";
import { useLoginModal } from "../contexts/LoginModalContext";
import { isIosCapacitor, httpRequestJson } from "../utils/nativeHttp";
import {
  getCurrentEffectiveSession,
  isEffectiveSessionLoggedIn,
} from "../auth/effectiveSession";
import {
  buildLoginState,
  getSafeAuthRedirectPath,
} from "../auth/authNavigation";
import {
  getCurrentAuthIdToken,
  getProfileCompletionCache,
  setProfileCompletionCache,
} from "../auth/socialAuth";

const LOOP_RESOLVE_PROFILE_URL =
  "https://us-central1-web-app-d9799.cloudfunctions.net/resolveAuthProfileHttpR31";

export default function ProtectedRoute({
  children,
  requireCompleteProfile = true,
}) {
  const location = useLocation();
  const from = getSafeAuthRedirectPath(
    `${location.pathname || "/"}${location.search || ""}`,
    "/",
  );

  const effectiveSession = useMemo(() => {
    return getCurrentEffectiveSession({
      firebaseAuth: auth,
      userInfo: {},
      isIosRuntime: isIosCapacitor(),
    });
  }, [location.pathname, location.search]);

  const loggedIn = isEffectiveSessionLoggedIn(effectiveSession);
  const uid = String(effectiveSession?.user?.uid || "").trim();
  const [profileState, setProfileState] = useState(
    requireCompleteProfile ? "checking" : "complete",
  );

  const safeUseLoginModal =
    typeof useLoginModal === "function"
      ? useLoginModal
      : () => ({ openLogin: null });
  const { openLogin } = safeUseLoginModal();

  useEffect(() => {
    if (loggedIn || isIosCapacitor()) return;
    if (openLogin) {
      try {
        openLogin(from);
      } catch (_) {}
    }
  }, [loggedIn, openLogin, from]);

  useEffect(() => {
    let mounted = true;

    if (!loggedIn || !requireCompleteProfile) {
      setProfileState(requireCompleteProfile ? "checking" : "complete");
      return () => {
        mounted = false;
      };
    }

    if (!uid) {
      setProfileState("incomplete");
      return () => {
        mounted = false;
      };
    }

    const cached = getProfileCompletionCache(uid);
    if (cached === true) {
      setProfileState("complete");
      return () => {
        mounted = false;
      };
    }
    if (cached === false) {
      setProfileState("incomplete");
      return () => {
        mounted = false;
      };
    }

    setProfileState("checking");

    (async () => {
      try {
        const idToken = await getCurrentAuthIdToken();
        const response = await httpRequestJson({
          url: LOOP_RESOLVE_PROFILE_URL,
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
          data: { source: "protected_route_r31" },
        });
        const complete = response?.profileComplete === true;
        setProfileCompletionCache(uid, complete);
        if (mounted) setProfileState(complete ? "complete" : "incomplete");
      } catch (error) {
        console.warn("[ProtectedRoute R31] profile check failed", error);
        // Fail open for an already-authenticated user when the network is temporarily down.
        // Social login itself still routes new accounts through profile completion.
        if (mounted) setProfileState("complete");
      }
    })();

    return () => {
      mounted = false;
    };
  }, [loggedIn, requireCompleteProfile, uid]);

  if (!loggedIn) {
    if (isIosCapacitor()) {
      return <Navigate to="/login" state={buildLoginState(from)} replace />;
    }
    if (openLogin) return null;
    return <Navigate to="/login" state={buildLoginState(from)} replace />;
  }

  if (requireCompleteProfile && profileState === "checking") {
    return (
      <div
        aria-label="Verificando perfil"
        style={{ minHeight: "100dvh", background: "#ffffff" }}
      />
    );
  }

  if (requireCompleteProfile && profileState === "incomplete") {
    return (
      <Navigate
        to={`/complete-profile?next=${encodeURIComponent(from)}`}
        state={{ from }}
        replace
      />
    );
  }

  return children;
}
